import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import logo from '../assets/logo.png';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  const navLinks = [
    { 
      name: 'Home', 
      path: '/', 
      icon: <path strokeLinecap="round" strokeLinejoin="round" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" /> 
    },
    { 
      name: 'Work', 
      path: '/work', 
      icon: <path strokeLinecap="round" strokeLinejoin="round" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /> 
    },
    { 
      name: 'Events', 
      path: '/events', 
      icon: <path strokeLinecap="round" strokeLinejoin="round" d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" /> 
    },
    { 
      name: 'Calendar', 
      path: '/calendar', 
      icon: <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /> 
    },
    { 
      name: 'Donate', 
      path: '/donate', 
      icon: <path strokeLinecap="round" strokeLinejoin="round" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" /> 
    },
  ];

  return (
    <nav className="bg-[#2C1A14] text-white sticky top-0 z-50 w-full border-b border-[#D4B896]/20 shadow-md backdrop-blur-md bg-opacity-95 overflow-visible">
      <div className="max-w-7xl mx-auto flex items-center justify-between px-4 sm:px-6 h-20 relative">
        
        {/* BRAND SECTION WITH POP-OUT LOGO */}
        <div className="flex items-center min-w-0">
          <Link to="/" className="flex items-center gap-3 no-underline group relative">
            {/* Logo container allows scale/height expansion without altering navbar layout */}
            <div className="relative flex items-center justify-center shrink-0">
              <img 
                src={logo} 
                alt="MBA Trust Logo" 
                className="h-16 sm:h-20 md:h-24 w-auto object-contain transition-transform duration-300 group-hover:scale-105 drop-shadow-md" 
              />
            </div>
            <div className="flex flex-col justify-center">
              <span className="font-serif text-lg sm:text-2xl font-bold text-[#D4B896] tracking-wider whitespace-nowrap leading-none">
                MBA TRUST
              </span>
              <span className="text-[9px] uppercase tracking-[0.25em] text-stone-400 font-sans mt-1 hidden sm:block">
                Legacy & Welfare
              </span>
            </div>
          </Link>
        </div>

        {/* DESKTOP NAVIGATION LINKS */}
        <div className="hidden lg:flex items-center space-x-1 sm:space-x-2">
          {navLinks.map((link) => {
            const isActive = location.pathname === link.path;
            return (
              <Link
                key={link.name}
                to={link.path}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs uppercase tracking-widest transition-all duration-200 font-medium ${
                  isActive
                    ? 'bg-[#D4B896] text-[#2C1A14] font-bold shadow-sm'
                    : 'text-stone-300 hover:text-white hover:bg-white/5'
                }`}
              >
                <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                  {link.icon}
                </svg>
                <span>{link.name}</span>
              </Link>
            );
          })}
        </div>

        {/* MOBILE HAMBURGER BUTTON */}
        <div className="lg:hidden flex items-center">
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="p-2 text-[#D4B896] hover:text-white rounded-lg focus:outline-none shrink-0"
            aria-label="Toggle navigation"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {isOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16m-7 6h7" />
              )}
            </svg>
          </button>
        </div>
      </div>

      {/* MOBILE DROPDOWN MENU */}
      {isOpen && (
        <div className="lg:hidden bg-[#2C1A14] border-t border-[#D4B896]/20 px-6 py-4 space-y-2 shadow-2xl animate-fadeIn">
          {navLinks.map((link) => {
            const isActive = location.pathname === link.path;
            return (
              <Link
                key={link.name}
                to={link.path}
                onClick={() => setIsOpen(false)}
                className={`flex items-center gap-3 px-4 py-3 rounded-xl text-xs uppercase tracking-widest transition-colors font-medium ${
                  isActive
                    ? 'bg-[#D4B896] text-[#2C1A14] font-bold'
                    : 'text-stone-300 hover:text-white hover:bg-white/5'
                }`}
              >
                <svg className="w-5 h-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                  {link.icon}
                </svg>
                {link.name}
              </Link>
            );
          })}
        </div>
      )}
    </nav>
  );
};

export default Navbar;